import React, {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import wrap from 'wraptile';
import {curry} from 'ramda';
import ListAltIcon from '@material-ui/icons/ListAlt';
import CircularProgress from '@material-ui/core/CircularProgress';
import QCToolsPublishingRights from './QCToolsPublishingRights';
import {
    PublishingRights,
    SetCurrentTab,
    SetQCToolsPublishingRights,
    HandleChangeEvent,
} from './QCToolsPublishingRightsTypes';
import QCToolsPublishingRightsItem from './QCToolsPublishingRightsItem';

interface QCToolsPublishingRightsContainerProps {
    publishingRights: PublishingRights[],
    setQCToolsPublishingRights: SetQCToolsPublishingRights;
    resetQCToolsPublishingRights: () => void,
    isLoading: boolean;
}

export default function QCToolsPublishingRightsContainer(props: QCToolsPublishingRightsContainerProps) {
    const {
        publishingRights,
        setQCToolsPublishingRights,
        isLoading,
        resetQCToolsPublishingRights,
    } = props;
    
    const {id} = useParams<{id: string}>();
    const fetchPublishingRights = onEffect(
        setQCToolsPublishingRights,
        resetQCToolsPublishingRights,
        id,
    );
    
    useEffect(fetchPublishingRights, []);
    
    const territorialTabsKeys: string[] = getTerritorialTabsKeys(publishingRights);
    
    const tabsPanel = getTabsPanel(publishingRights, territorialTabsKeys);
    
    if (isLoading) {
        return <CircularProgress />;
    }
    
    if (!publishingRights.length) {
        return (
            <div className="publishing-rights-empty">
                <ListAltIcon />
                <p>No Rights available</p>
            </div>
        );
    }
    
    return (
        <QCToolsPublishingRights
            tabs={territorialTabsKeys}
            tabsPanel={tabsPanel}
        />
    );
}

export const onEffect = wrap((setQCToolsPublishingRights: SetQCToolsPublishingRights, resetQCToolsPublishingRights: () => void, id: number) => {
    setQCToolsPublishingRights(id);
    return resetQCToolsPublishingRights();
});

export const handleChange = curry((setCurrentTab: SetCurrentTab, event: HandleChangeEvent, newValue: string) => {
    setCurrentTab(newValue);
});

const mapKeys = curry((item) => item.countryCode);

const getTerritorialTabsKeys = (publishingRights: PublishingRights[]) => {
    const territoriesArray = publishingRights.map(mapKeys);
    
    return Array.from(new Set(territoriesArray));
};

export const getItemByKey = curry((key, item) => {
    return item.countryCode === key;
});

export const mapTabsByKeys = curry((key, publishingRightsItem) => {
    const {
        rightsType,
        availabilityStart,
        availabilityEnd,
        screenings,
        id,
    } = publishingRightsItem;
    
    return (
        <div
            key={id}
            className="publishing-rights"
        >
            <QCToolsPublishingRightsItem
                rightsType={rightsType}
                availabilityStart={availabilityStart}
                availabilityEnd={availabilityEnd}
                screenings={screenings}
            />
        </div>
    );
});

export const mapToTabsPanel = curry((publishingRights, key) => {
    return publishingRights.filter(getItemByKey(key)).map(mapTabsByKeys(key));
});

export const getTabsPanel = (publishingRights: PublishingRights[], keys: string[]) => {
    const withPublishing = mapToTabsPanel(publishingRights);
    return keys.map(withPublishing);
};
